import { EmptyBlockContent, InputOption, Loading, SelectDropdownInput, Table } from '@tradingblock/components';
import { AccountDetails } from '@tradingblock/types';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AccountManagementDataActions } from '../../../../data/global/actions/AccountManagementActions';
import { useStateSelector, uiApiToken } from '../../../../data/global/dataSelectors';
import dayjs from 'dayjs';
import { RootAction } from '../../../../data/global/actions';
import Config from '../../../../config';
import { buildStore, TokenFromStorage } from '@tradingblock/storage';
import { Cell } from 'react-table';
import {
  buildLoginHistoryCsv,
  buildMapsUrlForGeo,
  buildRowSecurityMeta,
  countryCodeToFlagEmoji,
  downloadTextFile,
  getProfileCountryForGeo,
  lookupIpGeo,
  parseIpGeoResponse,
  shouldSkipExternalIpGeolocationLookup,
} from './authHistoryTableGeoUtils';

/** Local aliases: avoids `import type` / `type` in import lists (not supported by this UI build). */
type IpGeoDetails = import('./authHistoryTableGeoUtils').IpGeoDetails;
type RowSecurityMeta = import('./authHistoryTableGeoUtils').RowSecurityMeta;

/** Row shape from `User/GetLoginHistory` (Legacy API `Payload` array). */
interface AuthHistoryRow {
  TimeStamp: string;
  Ip?: string;
  ipAddress?: string;
}

type AuthHistoryTableRow = AuthHistoryRow & {
  _idx: number;
  _ipGeo?: IpGeoDetails;
  _ipGeoLoading: boolean;
  _rowSecurity: RowSecurityMeta;
};

function getIpString(row: AuthHistoryRow): string {
  return (row.Ip || row.ipAddress || '').trim();
}

function displayOrDash(value: string | undefined): string {
  return value && value.trim() !== '' ? value.trim() : '—';
}

/** RestHub sometimes stores the array directly; guard if a wrapped payload ever lands in Redux. */
function normalizeLoginHistoryPayload(data: unknown): AuthHistoryRow[] {
  if (Array.isArray(data)) {
    return data as AuthHistoryRow[];
  }
  if (data && typeof data === 'object' && Array.isArray((data as { Payload?: unknown }).Payload)) {
    return (data as { Payload: AuthHistoryRow[] }).Payload;
  }
  return [];
}

const localStorageEngine = buildStore({ kind: 'localstorage' });

async function resolveDashboardJwt(reduxUiToken: string | undefined): Promise<string | undefined> {
  if (reduxUiToken && reduxUiToken.trim() !== '') {
    return reduxUiToken.trim();
  }
  try {
    const fromLs = await TokenFromStorage(localStorageEngine);
    return fromLs && fromLs.trim() !== '' ? fromLs.trim() : undefined;
  } catch {
    return undefined;
  }
}

const shimmerKeyframes = `
@keyframes auth-ip-geo-shimmer {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}`;

if (typeof document !== 'undefined' && !document.getElementById('auth-ip-geo-shimmer-style')) {
  const s = document.createElement('style');
  s.id = 'auth-ip-geo-shimmer-style';
  s.textContent = shimmerKeyframes;
  document.head.appendChild(s);
}

/**
 * Non-empty "All" value for country/IP filters. `SelectDropdownInput` + FloatingLabel use
 * `isFieldValueFilled('')` === false, so the floating title stays visible and overlaps the
 * selected "All" label. A sentinel string keeps the field "filled" while still meaning no filter.
 */
const FILTER_COUNTRY_IP_ALL = '*';

export const AuthHistoryTable: React.FunctionComponent<Record<string, never>> = () => {
  const dispatch = useDispatch<React.Dispatch<RootAction>>();

  const username = useStateSelector(s => s.auth.userName);
  const accountId = useStateSelector(s => s.account.accountId);
  const selectedLinkedAccountId = useStateSelector(
    s => s.account.selectedLinkedAccount && s.account.selectedLinkedAccount.accountId
  );
  const effectiveAccountId = selectedLinkedAccountId != null ? selectedLinkedAccountId : accountId;
  const accountDetailsLoaded = useStateSelector(s => s.accountManagement.accountDetails.isLoaded);
  const accountDetailsData = useStateSelector(
    s => s.accountManagement.accountDetails.data
  ) as AccountDetails | undefined;
  const authHistoryRaw = useStateSelector(s => s.accountManagement.authHistory.data);
  const isFetching = useStateSelector(s => s.accountManagement.authHistory.isFetching);
  const token = useStateSelector(uiApiToken);
  const ipLocationByIp = useStateSelector(
    s => s.accountManagement.authHistoryIpGeo.byIp
  ) as Record<string, IpGeoDetails>;
  const ipLookupInFlight = useStateSelector(
    s => s.accountManagement.authHistoryIpGeo.isLookupInFlight
  );
  const sessionClientIp = useStateSelector(
    s => s.accountManagement.authHistoryIpGeo.sessionClientIp
  );
  const sessionClientIpSource = useStateSelector(
    s => s.accountManagement.authHistoryIpGeo.sessionClientIpSource
  );

  /** Avoid hammering GET /client-ip; reset when auth token changes. */
  const clientIpFetchAttemptedRef = useRef(false);

  const [timeFilter, setTimeFilter] = useState<'all' | '7d'>('all');
  const [countryFilter, setCountryFilter] = useState<string>(FILTER_COUNTRY_IP_ALL);
  const [ipFilter, setIpFilter] = useState<string>(FILTER_COUNTRY_IP_ALL);
  /** Parent-owned pagination: survives row ref churn from IP geolocation and avoids react-table client paging resync. */
  const [tablePageIndex, setTablePageIndex] = useState(0);
  const [tablePageSize, setTablePageSize] = useState(10);

  const authHistory = useMemo(() => normalizeLoginHistoryPayload(authHistoryRaw), [authHistoryRaw]);

  const profileCountry = useMemo(
    () => getProfileCountryForGeo(accountDetailsData),
    [accountDetailsData]
  );

  const uniqueIpsKey = useMemo(() => {
    if (!authHistory || authHistory.length === 0) {
      return '';
    }
    const s = new Set<string>();
    for (const row of authHistory) {
      const ip = getIpString(row);
      if (ip) {
        s.add(ip);
      }
    }
    return Array.from(s).sort().join(',');
  }, [authHistory]);

  const uniqueIps = useMemo(() => (uniqueIpsKey ? uniqueIpsKey.split(',') : []), [uniqueIpsKey]);

  /** IP keys not present in the Redux map — skip network when the user returns with a warm cache. */
  const missingIpGeoIps = useMemo(
    () =>
      uniqueIps.filter(
        ip => !ipLocationByIp[ip] && !shouldSkipExternalIpGeolocationLookup(ip)
      ),
    [uniqueIps, ipLocationByIp]
  );

  const baseOrdered = useMemo(() => {
    return [...authHistory].sort((a, b) => {
      const ta = dayjs(a.TimeStamp + (a.TimeStamp.indexOf('Z') >= 0 || a.TimeStamp.indexOf('+') >= 0 ? '' : 'Z'));
      const tb = dayjs(b.TimeStamp + (b.TimeStamp.indexOf('Z') >= 0 || b.TimeStamp.indexOf('+') >= 0 ? '' : 'Z'));
      return tb.valueOf() - ta.valueOf();
    });
  }, [authHistory]);

  /**
   * IP used only for the “Session” badge: **GET /client-ip** when it returns a public/comparable address.
   * If `/client-ip` is loopback/private (API sees only an internal hop), we **do not** substitute the newest
   * login row — that matched RestHub’s stored egress IP (e.g. 38.x) and falsely marked every row with that IP as “Session”.
   */
  const sessionIpForSecurityBadge = useMemo(() => {
    const raw = sessionClientIp != null ? String(sessionClientIp).trim() : '';
    if (raw !== '' && !shouldSkipExternalIpGeolocationLookup(raw)) {
      return raw;
    }
    return undefined;
  }, [sessionClientIp]);

  /** True when GET /client-ip returns a private/internal address (same condition that suppresses the Session badge). */
  const showPrivateSessionIpExplainer = useMemo(() => {
    const raw = sessionClientIp != null ? String(sessionClientIp).trim() : '';
    return raw !== '' && shouldSkipExternalIpGeolocationLookup(raw);
  }, [sessionClientIp]);

  const allGeoCodesNewestFirst = useMemo(
    () =>
      baseOrdered.map(r => {
        const g = lookupIpGeo(ipLocationByIp, getIpString(r));
        return g && g.countryCode2 ? g.countryCode2 : undefined;
      }),
    [baseOrdered, ipLocationByIp]
  );

  const securityList = useMemo((): RowSecurityMeta[] => {
    return baseOrdered.map((row, i) => {
      const ip = getIpString(row);
      const geo = lookupIpGeo(ipLocationByIp, ip);
      const older = baseOrdered.slice(i + 1, i + 1 + 24).map(r => {
        const g = lookupIpGeo(ipLocationByIp, getIpString(r));
        return g && g.countryCode2 ? g.countryCode2 : undefined;
      });
      return buildRowSecurityMeta(
        ip,
        profileCountry,
        geo,
        allGeoCodesNewestFirst,
        older,
        sessionIpForSecurityBadge
      );
    });
  }, [
    baseOrdered,
    ipLocationByIp,
    profileCountry,
    allGeoCodesNewestFirst,
    sessionIpForSecurityBadge,
  ]);

  const countryOptions = useMemo(() => {
    const s = new Set<string>();
    for (const c of allGeoCodesNewestFirst) {
      if (c) {
        s.add(c);
      }
    }
    return Array.from(s).sort();
  }, [allGeoCodesNewestFirst]);

  const timeSelectOptions = useMemo((): InputOption[] => {
    return [
      { value: 'all', label: 'All', raw_value: 'all' },
      { value: '7d', label: 'Last 7 days', raw_value: '7d' },
    ];
  }, []);

  const countrySelectOptions = useMemo((): InputOption[] => {
    return [
      { value: FILTER_COUNTRY_IP_ALL, label: 'All', raw_value: FILTER_COUNTRY_IP_ALL },
      ...countryOptions.map(c => ({ value: c, label: c, raw_value: c })),
    ];
  }, [countryOptions]);

  const ipSelectOptions = useMemo((): InputOption[] => {
    return [
      { value: FILTER_COUNTRY_IP_ALL, label: 'All', raw_value: FILTER_COUNTRY_IP_ALL },
      ...uniqueIps.map(ip => ({ value: ip, label: ip, raw_value: ip })),
    ];
  }, [uniqueIps]);

  const rowsBeforeFilter = useMemo((): AuthHistoryTableRow[] => {
    return baseOrdered.map((r, i) => {
      const ip = getIpString(r);
      return {
        TimeStamp: r.TimeStamp,
        Ip: r.Ip,
        ipAddress: r.ipAddress,
        _idx: i,
        _ipGeo: lookupIpGeo(ipLocationByIp, ip),
        _ipGeoLoading: ipLookupInFlight,
        _rowSecurity: securityList[i] || {
          unusual: false,
          sameSession: false,
          showWasThisYou: false,
        },
      };
    });
  }, [baseOrdered, ipLocationByIp, ipLookupInFlight, securityList]);

  const filteredTableRows = useMemo(() => {
    const cutoff7d = dayjs().subtract(7, 'day');
    return rowsBeforeFilter.filter(row => {
      if (timeFilter === '7d') {
        const t = dayjs(
          row.TimeStamp + (row.TimeStamp.indexOf('Z') >= 0 || row.TimeStamp.indexOf('+') >= 0 ? '' : 'Z')
        );
        if (!t.isAfter(cutoff7d)) {
          return false;
        }
      }
      if (
        countryFilter !== FILTER_COUNTRY_IP_ALL &&
        ((row._ipGeo && row._ipGeo.countryCode2) || '') !== countryFilter
      ) {
        return false;
      }
      if (ipFilter !== FILTER_COUNTRY_IP_ALL && getIpString(row) !== ipFilter) {
        return false;
      }
      return true;
    });
  }, [rowsBeforeFilter, timeFilter, countryFilter, ipFilter]);

  const filteredCount = filteredTableRows.length;
  const tablePageCount = useMemo(
    () => (filteredCount === 0 ? 1 : Math.max(1, Math.ceil(filteredCount / tablePageSize))),
    [filteredCount, tablePageSize]
  );

  const pagedTableRows = useMemo(() => {
    if (filteredCount === 0) {
      return [];
    }
    const start = tablePageIndex * tablePageSize;
    return filteredTableRows.slice(start, start + tablePageSize);
  }, [filteredTableRows, filteredCount, tablePageIndex, tablePageSize]);

  useEffect(() => {
    setTablePageIndex(0);
  }, [timeFilter, countryFilter, ipFilter, username]);

  useEffect(() => {
    const maxIndex = tablePageCount - 1;
    if (tablePageIndex > maxIndex) {
      setTablePageIndex(Math.max(0, maxIndex));
    }
  }, [tablePageCount, tablePageIndex, filteredCount]);

  const onTablePage = useCallback((pageIndex: number) => {
    setTablePageIndex(pageIndex);
  }, []);

  const onTablePageSize = useCallback((pageSize: number) => {
    setTablePageSize(pageSize);
    setTablePageIndex(0);
  }, []);

  const fetchAuthHistory = useCallback(() => {
    dispatch(AccountManagementDataActions.requestAuthHistory({ username }));
  }, [dispatch, username]);

  useEffect(() => {
    fetchAuthHistory();
  }, [fetchAuthHistory]);

  useEffect(() => {
    if (effectiveAccountId && !accountDetailsLoaded) {
      dispatch(AccountManagementDataActions.requestAccountDetails({ accountId: effectiveAccountId }));
    }
  }, [effectiveAccountId, accountDetailsLoaded, dispatch]);

  useEffect(() => {
    if (uniqueIpsKey === '' || uniqueIps.length === 0) {
      if (!isFetching) {
        dispatch(AccountManagementDataActions.clearAuthHistoryLoginIpGeo());
      }
      return;
    }
    if (missingIpGeoIps.length === 0) {
      return;
    }
    let cancelled = false;
    dispatch(AccountManagementDataActions.requestAuthHistoryIpGeo());
    (async () => {
      try {
        const authJwt = await resolveDashboardJwt(token);
        if (!authJwt) {
          if (!cancelled) {
            dispatch(AccountManagementDataActions.failAuthHistoryIpGeo({ clearByIp: false }));
          }
          console.warn('[AuthHistoryTable] No dashboard JWT; skipping IP geolocation');
          return;
        }
        const base = Config.uiApi.replace(/\/+$/, '');
        const url = `${base}/ip-geo/lookup`;
        const res = await fetch(url, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: authJwt,
          },
          body: JSON.stringify({ ips: missingIpGeoIps }),
        });
        if (!res.ok) {
          await res.text();
          if (!cancelled) {
            dispatch(AccountManagementDataActions.failAuthHistoryIpGeo({ clearByIp: true }));
          }
          console.warn('[AuthHistoryTable] IP geolocation request failed', res.status);
          return;
        }
        const raw = await res.json();
        const map = parseIpGeoResponse(raw, missingIpGeoIps);
        if (!cancelled) {
          dispatch(AccountManagementDataActions.mergeAuthHistoryIpGeo({ merge: map }));
        }
      } catch (e) {
        if (!cancelled) {
          dispatch(AccountManagementDataActions.failAuthHistoryIpGeo({ clearByIp: true }));
        }
        const msg = e instanceof Error ? e.message : 'unknown error';
        console.error('[AuthHistoryTable] IP geolocation error', msg);
      } finally {
        if (cancelled) {
          dispatch(AccountManagementDataActions.endAuthHistoryIpGeoLookup());
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, uniqueIpsKey, uniqueIps, missingIpGeoIps, dispatch, isFetching]);

  useEffect(() => {
    clientIpFetchAttemptedRef.current = false;
  }, [token]);

  useEffect(() => {
    const raw = sessionClientIp != null ? String(sessionClientIp).trim() : '';
    const hasComparableSessionIp =
      raw !== '' && !shouldSkipExternalIpGeolocationLookup(raw);
    if (hasComparableSessionIp) {
      return;
    }
    if (clientIpFetchAttemptedRef.current) {
      return;
    }
    let cancelled = false;
    (async () => {
      const authJwt = await resolveDashboardJwt(token);
      if (!authJwt) {
        if (!cancelled) {
          dispatch(AccountManagementDataActions.setAuthHistorySessionClientIp({ ip: undefined }));
        }
        console.warn('[AuthHistoryTable] No dashboard JWT; skipping client-ip for session match');
        return;
      }
      const base = Config.uiApi.replace(/\/+$/, '');
      const url = `${base}/client-ip`;
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: { Accept: 'application/json', Authorization: authJwt },
        });
        if (!res.ok) {
          if (!cancelled) {
            dispatch(AccountManagementDataActions.setAuthHistorySessionClientIp({ ip: undefined }));
          }
          console.warn('[AuthHistoryTable] client-ip request failed', res.status);
          return;
        }
        const data = (await res.json()) as { ip?: string; source?: string };
        const ip = data && data.ip ? String(data.ip).trim() : undefined;
        const source =
          data && typeof data.source === 'string' && data.source.trim() !== '' ? data.source.trim() : undefined;
        if (!cancelled) {
          dispatch(AccountManagementDataActions.setAuthHistorySessionClientIp({ ip, source }));
        }
      } catch (e) {
        if (!cancelled) {
          dispatch(AccountManagementDataActions.setAuthHistorySessionClientIp({ ip: undefined }));
        }
        const msg = e instanceof Error ? e.message : 'unknown error';
        console.error('[AuthHistoryTable] client-ip error', msg);
      } finally {
        if (!cancelled) {
          clientIpFetchAttemptedRef.current = true;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, dispatch, sessionClientIp]);

  const onExportCsv = useCallback(() => {
    const rows = filteredTableRows.map(r => ({
      TimeStamp: r.TimeStamp,
      ip: getIpString(r),
      geo: r._ipGeo,
    }));
    const csv = buildLoginHistoryCsv(rows);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadTextFile(`login-history-${stamp}.csv`, csv, 'text/csv;charset=utf-8');
  }, [filteredTableRows]);

  const copyToClipboard = useCallback((text: string) => {
    (async () => {
      try {
        await navigator.clipboard.writeText(text);
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'unknown';
        console.error('[AuthHistoryTable] clipboard error', msg);
      }
    })();
  }, []);

  const formatRowText = useCallback((row: AuthHistoryTableRow): string => {
    const ip = getIpString(row);
    const when = dayjs(
      row.TimeStamp + (row.TimeStamp.indexOf('Z') >= 0 || row.TimeStamp.indexOf('+') >= 0 ? '' : 'Z')
    );
    const date = when.isValid() ? when.format('MM/DD/YYYY h:mm:ss A') : row.TimeStamp;
    const g = row._ipGeo;
    return [date, ip, g ? g.label : '', g ? g.countryCode2 : '', g ? g.org : ''].filter(Boolean).join(' | ');
  }, []);

  const AuthHistoryColumns = useMemo(
    () => [
      {
        Header: 'Date',
        accessor: (a: AuthHistoryTableRow) => a.TimeStamp,
        Cell: ({ value }: { value: string }) => {
          return dayjs(
            value + (value && (value.indexOf('Z') >= 0 || value.indexOf('+') >= 0) ? '' : 'Z')
          ).format('MM/DD/YYYY');
        },
      },
      {
        Header: 'Time',
        accessor: (a: AuthHistoryTableRow) => a.TimeStamp,
        Cell: ({ value }: { value: string }) => {
          return dayjs(
            value + (value && (value.indexOf('Z') >= 0 || value.indexOf('+') >= 0) ? '' : 'Z')
          ).format('h:mm:ss A');
        },
      },
      {
        id: 'recordedIp',
        Header: () => (
          <span title="Address stored by our sign-in service for each event (may differ from your VPN session address).">
            Recorded IP
          </span>
        ),
        accessor: (a: AuthHistoryTableRow) => a.Ip || a.ipAddress,
        Cell: function AuthHistoryIpCell({ row }: Cell<AuthHistoryTableRow>) {
          const o = row.original;
          const ip = getIpString(o);
          if (!ip) {
            return (
              <span className="d-inline-flex align-items-center mute">
                <i className="fas fa-ban" aria-hidden={true} />
                <span className="sr-only">No IP recorded for this sign-in</span>
              </span>
            );
          }
          return (
            <div
              className="auth-history-table__ip-cell w-100 d-flex flex-nowrap align-items-center"
              style={{ minWidth: 0 }}
            >
              <div
                className="auth-history-table__ip-group d-flex flex-nowrap align-items-center w-100"
                style={{ gap: '0.55rem', minWidth: 0 }}
              >
                <span
                  className="auth-history-table__ip text-truncate d-block"
                  style={{ minWidth: 0, flex: '1 1 auto' }}
                  title={ip}
                >
                  {ip}
                </span>
                <button
                  type="button"
                  className="btn btn-link p-0 auth-history-ico flex-shrink-0 auth-history-ico--ip-copy"
                  title="Copy IP address"
                  onClick={e => {
                    e.stopPropagation();
                    copyToClipboard(ip);
                  }}
                >
                  <i className="fal fa-copy" style={{ fontSize: '0.9rem' }} aria-hidden={true} />
                  <span className="sr-only">Copy IP address</span>
                </button>
              </div>
            </div>
          );
        },
      },
      {
        Header: 'Location',
        id: 'location',
        accessor: (a: AuthHistoryTableRow) => (a._ipGeo && a._ipGeo.label ? a._ipGeo.label : ''),
        Cell: function AuthHistoryLocationCell({ row }: Cell<AuthHistoryTableRow>) {
          const o = row.original;
          const geo = o._ipGeo;
          const where = geo && geo.label ? geo.label : '';
          const showShimmer = o._ipGeoLoading && !where;
          if (showShimmer) {
            return (
              <div className="d-flex flex-nowrap align-items-center" style={{ gap: '0.35rem' }}>
                <span
                  className="d-inline-block rounded"
                  style={{
                    minWidth: 72,
                    maxWidth: '100%',
                    height: 10,
                    background:
                      'linear-gradient(90deg, rgba(0,0,0,0.04) 0px, rgba(0,0,0,0.1) 40px, rgba(0,0,0,0.04) 80px)',
                    backgroundSize: '200% 100%',
                    animation: 'auth-ip-geo-shimmer 1.4s ease-in-out infinite',
                  }}
                />
                <span className="txt-xs mute text-nowrap">…</span>
              </div>
            );
          }
          if (!where) {
            return <span className="mute">—</span>;
          }
          const flag = countryCodeToFlagEmoji(geo && geo.countryCode2 ? geo.countryCode2 : undefined);
          return (
            <div
              className="d-flex flex-nowrap align-items-center auth-history-table__location w-100"
              style={{ gap: 6, minWidth: 0 }}
              title={where}
            >
              {!!flag && (
                <span className="auth-history-table__flag flex-shrink-0" aria-hidden="true">
                  {flag}
                </span>
              )}
              <span className="auth-history-one-line" style={{ minWidth: 0, flex: '1 1 auto' }}>
                {where}
              </span>
            </div>
          );
        },
      },
      {
        Header: 'Network',
        id: 'network',
        accessor: (a: AuthHistoryTableRow) => (a._ipGeo && a._ipGeo.org ? a._ipGeo.org : ''),
        Cell: function AuthHistoryNetworkCell({ row }: Cell<AuthHistoryTableRow>) {
          const org = row.original._ipGeo && row.original._ipGeo.org;
          const text = displayOrDash(org);
          return (
            <span
              className="auth-history-table__wrap auth-history-one-line txt-sm d-block"
              title={org && org.trim() !== '' ? org : undefined}
            >
              {text}
            </span>
          );
        },
      },
      {
        Header: 'Time zone',
        id: 'timeZone',
        accessor: (a: AuthHistoryTableRow) => (a._ipGeo && a._ipGeo.timeZone ? a._ipGeo.timeZone : ''),
        Cell: function AuthHistoryTimeZoneCell({ row }: Cell<AuthHistoryTableRow>) {
          const tz = row.original._ipGeo && row.original._ipGeo.timeZone;
          const text = displayOrDash(tz);
          return (
            <span
              className="auth-history-table__wrap auth-history-one-line txt-sm d-block"
              title={tz && tz.trim() !== '' ? tz : undefined}
            >
              {text}
            </span>
          );
        },
      },
      {
        Header: 'Security',
        id: 'security',
        disableSortBy: true,
        accessor: () => '',
        Cell: function AuthHistorySecurityCell({ row }: Cell<AuthHistoryTableRow>) {
          const o = row.original;
          const geo = o._ipGeo;
          const mapsUrl = buildMapsUrlForGeo(geo);
          const meta = o._rowSecurity;
          return (
            <div
              className="auth-history-table__security d-flex flex-wrap align-items-center w-100"
              style={{ gap: 6, minWidth: 0 }}
            >
              {meta.unusual && (
                <span
                  className="badge badge-warning auth-history-compact-badge"
                  title="This login does not match your profile or recent sign-in pattern"
                >
                  Unusual
                </span>
              )}
              {meta.sameSession && (
                <span className="badge badge-info auth-history-compact-badge" title="This IP matches your current network">
                  Session
                </span>
              )}
              {!!mapsUrl && (
                <a
                  className="auth-history-ico btn btn-link p-0"
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Map"
                  onClick={e => e.stopPropagation()}
                >
                  <i className="fal fa-map-marked-alt" style={{ fontSize: '0.95rem' }} aria-hidden={true} />
                  <span className="sr-only">Open in maps</span>
                </a>
              )}
              {meta.showWasThisYou && (
                <span className="txt-xs" style={{ minWidth: 0 }}>
                  <span className="mute">Was this you? </span>
                  <a href="#account-security-2fa" onClick={e => e.stopPropagation()}>
                    2FA
                  </a>
                  <span className="mute"> · </span>
                  <a href="#account-personal-reset-password" onClick={e => e.stopPropagation()}>
                    Password
                  </a>
                </span>
              )}
              <span
                className="auth-history-table__cell-spacer"
                aria-hidden={true}
              />
              <button
                type="button"
                className="btn btn-link p-0 auth-history-ico flex-shrink-0"
                title="Copy row (date, IP, location, details)"
                onClick={e => {
                  e.stopPropagation();
                  copyToClipboard(formatRowText(o));
                }}
              >
                <i className="fal fa-clipboard" style={{ fontSize: '0.95rem' }} aria-hidden={true} />
                <span className="sr-only">Copy row details</span>
              </button>
            </div>
          );
        },
      },
    ],
    [copyToClipboard, formatRowText]
  );

  const tableInitialState = useMemo(
    () => ({
      pageSize: tablePageSize,
      pageIndex: 0,
      sortBy: [],
      columnOrder: [],
    }),
    [tablePageSize]
  );

  return (
    <>
      {isFetching && (
        <div className="container-fluid" style={{ height: '100%' }}>
          <div className="row" style={{ alignItems: 'baseline' }}>
            <i className="fal fa-history fa-lg mr-2 dashboard-icon" /> <h5>Log-in History</h5>
          </div>
          <div className="row">
            <Loading size="medium" /> <span className="mute ml-2">Loading...</span>
          </div>
        </div>
      )}
      {!isFetching && authHistory && authHistory.length === 0 && <EmptyBlockContent />}
      {!isFetching && authHistory && authHistory.length > 0 && (
        <div className="container-fluid" style={{ height: '100%' }}>
          <div className="row align-items-start justify-content-between auth-history-filters-row">
            <div className="col-12 col-md-auto d-flex flex-column mb-2 mb-md-0 pl-0">
              <div className="d-flex align-items-baseline">
                <i className="fal fa-history fa-lg mr-2 dashboard-icon" />
                <h5 className="mb-0" id="auth-history-section-title">
                  Log-in History
                </h5>
              </div>
              <p className="txt-sm mute mb-0 mt-1 pr-md-3" style={{ maxWidth: 640 }}>
                Sign-in history lists the IP our authentication service stored for each login. &ldquo;Unusual&rdquo;
                highlights sign-ins that don&apos;t match your account profile or recent pattern. The
                &ldquo;Session&rdquo; badge appears only when your current{' '}
                <abbr title="Resolved by the dashboard API from trusted proxy headers">GET /client-ip</abbr> address
                matches a row; both must be public IPs. On corporate networks or VPN, see the notice below if your
                current session shows a private address.
              </p>
              {showPrivateSessionIpExplainer && sessionClientIp && (
                <div
                  className="alert alert-info txt-sm mb-0 mt-3 pr-md-3"
                  style={{ maxWidth: 720 }}
                  role="status"
                  aria-live="polite"
                >
                  <p className="font-weight-bold mb-2">Corporate or VPN session</p>
                  <p className="mb-2">
                    <strong>Address we see for your browser right now:</strong>{' '}
                    <code className="txt-sm">{sessionClientIp}</code>
                    {sessionClientIpSource ? (
                      <span className="mute"> (from {sessionClientIpSource})</span>
                    ) : null}
                    . This is a private or internal network address, not your public internet location.
                  </p>
                  <p className="mb-0">
                    <strong>Recorded IP column:</strong> Values are what the sign-in service stored. When we cannot send
                    it a public address, it often logs our <strong>shared outbound</strong> IP instead—so coworkers may
                    share the same row, and location or ISP (for example Cogent) may reflect our infrastructure, not you.
                  </p>
                </div>
              )}
            </div>
            <div className="col-12 col-md pl-md-0 position-relative">
              <div
                className="auth-history-toolbar"
                role="group"
                aria-label="Filter login history"
              >
                <div className="auth-history-toolbar__fields">
                  <div className="auth-history-toolbar__field auth-history-toolbar__field--time">
                    <label className="auth-history-toolbar__field-label" htmlFor="auth-history-time-filter">
                      Time
                    </label>
                    <SelectDropdownInput
                      name="auth-history-time-filter"
                      placeholder=""
                      value={timeFilter}
                      options={timeSelectOptions}
                      isSearchable={false}
                      menuPlacement="auto"
                      menuPortalTargetId="auth-history-filters-menu-root"
                      onchange={(_e, v) => {
                        setTimeFilter(String(v) === '7d' ? '7d' : 'all');
                      }}
                    />
                  </div>
                  <div className="auth-history-toolbar__field auth-history-toolbar__field--country">
                    <label className="auth-history-toolbar__field-label" htmlFor="auth-history-country-filter">
                      Country
                    </label>
                    <SelectDropdownInput
                      name="auth-history-country-filter"
                      placeholder=""
                      value={countryFilter}
                      options={countrySelectOptions}
                      isSearchable={false}
                      menuPlacement="auto"
                      menuPortalTargetId="auth-history-filters-menu-root"
                      onchange={(_e, v) => {
                        if (v == null) {
                          setCountryFilter(FILTER_COUNTRY_IP_ALL);
                        } else {
                          setCountryFilter(String(v));
                        }
                      }}
                    />
                  </div>
                  <div className="auth-history-toolbar__field auth-history-toolbar__field--ip">
                    <label className="auth-history-toolbar__field-label" htmlFor="auth-history-ip-filter">
                      IP address
                    </label>
                    <SelectDropdownInput
                      name="auth-history-ip-filter"
                      placeholder=""
                      value={ipFilter}
                      options={ipSelectOptions}
                      isSearchable={true}
                      menuPlacement="auto"
                      menuPortalTargetId="auth-history-filters-menu-root"
                      onchange={(_e, v) => {
                        if (v == null) {
                          setIpFilter(FILTER_COUNTRY_IP_ALL);
                        } else {
                          setIpFilter(String(v));
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="auth-history-toolbar__actions">
                  <button
                    type="button"
                    className="btn btn-sm auth-history-toolbar__export"
                    onClick={onExportCsv}
                  >
                    <i className="fal fa-file-export auth-history-toolbar__export-icon" aria-hidden={true} />
                    Export CSV
                  </button>
                </div>
              </div>
              <div
                id="auth-history-filters-menu-root"
                className="auth-history-menu-portal"
                aria-hidden={true}
              />
            </div>
          </div>
          <div className="row">
            <div className="col-12 auth-history-table-scroll">
              <Table
                tableKey="auth-history"
                numColumns={AuthHistoryColumns.length}
                columns={AuthHistoryColumns}
                data={pagedTableRows}
                columnWidths={['10%', '9%', '12%', '18%', '13%', '10%', '28%']}
                hasPagination={true}
                pageCount={tablePageCount}
                initialState={tableInitialState}
                onPage={onTablePage}
                onPageSize={onTablePageSize}
                responsiveDisabled
                sortable={true}
                manualPagination={true}
                manualSortBy={false}
                className="security-table auth-history-data-table"
              />
            </div>
          </div>
          <div className="row mt-2">
            <p className="mute txt-sm">
              The log-in history table displays the last 50 successful log-in attempts. If columns are cut off, scroll
              horizontally below the table header.
            </p>
          </div>
        </div>
      )}
    </>
  );
};
